import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {AsteriskModule} from 'src/app/directives/mark-asterisk/mark-asterisk.module';
import {UiKitsModule} from 'src/app/shared/ui-kits/ui-kits.module';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Utils} from 'src/app/shared/utils/utils';
import {filter} from 'rxjs';
import {WorkingPeriodService} from 'src/app/services/working-period/working-period.service';
import {
  IDecreasePeriodDropdowns,
  workingPeriodStatus,
} from 'src/app/interfaces/termination.interface';

@UntilDestroy()
@Component({
  selector: 'app-decrease-employment-position-form',
  standalone: true,
  imports: [
    CommonModule,
    UiKitsModule,
    ReactiveFormsModule,
    AsteriskModule,
  ],
  templateUrl: './decrease-employment-position-form.component.html',
  styleUrls: ['./decrease-employment-position-form.component.scss'],
})
export class DecreaseEmploymentPositionFormComponent
  implements OnInit, OnDestroy
{
  workingPeriodForm!: FormGroup;
  @Input() decreasePeriodDropdowns!: IDecreasePeriodDropdowns;

  constructor(protected workingPeriod: WorkingPeriodService) {}

  ngOnInit(): void {
    this.workingPeriodForm = this.workingPeriod.getWorkingPeriod;
    this.setEmployeeFormControls();
    this.initFormAction();
    this.subscribeToValueChanges();
  }

  ngOnDestroy(): void {
    this.removeEmployeeFormControls();
  }

  initFormAction() {
    this.setContractValidators();
    this.formerAccountCorrespondenceControlActions();
    this.updateFormOnStatusChange();
  }

  setEmployeeFormControls() {
    this.workingPeriodForm.addControl(
      'lastWorkingDay',
      new FormControl<string | null>(null, [Validators.required])
    );
    this.workingPeriodForm.addControl(
      'rate',
      new FormControl<number | null>(null, [
        Validators.max(999),
        Validators.min(0.1),
      ])
    );
    this.workingPeriodForm.addControl(
      'terminationReason',
      new FormControl<IDropdownObject | null>(null, [Validators.required])
    );
  }

  removeEmployeeFormControls() {
    this.workingPeriodForm.removeControl('lastWorkingDay');
    this.workingPeriodForm.removeControl('rate');
    this.workingPeriodForm.removeControl('terminationReason');
    this.workingPeriodForm.removeControl('accountCorrespondence');
  }

  subscribeToValueChanges() {
    this.workingPeriod
      .formControl('newStatus')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initFormAction();
      });

    this.workingPeriod
      .formControl('corporateAccount')!
      .valueChanges.pipe(
        untilDestroyed(this),
        filter(account => account)
      )
      .subscribe(() => {
        this.setAccountBlockDateValidator();
      });
  }

  updateFormOnStatusChange() {
    if (this.workingPeriod.newStatus == workingPeriodStatus.Former) {
      this.setDropdownOptionValues(
        'At the end of the last working day',
        'Needs to be returned on the last working day'
      );
    } else this.resetDropdownValues();
  }

  private setDropdownOptionValues(
    corporateAccountName: string,
    equipmentName: string
  ) {
    this.setFormControlValue(
      'corporateAccount',
      this.corporateAccountOptionsList.find(
        dropdown => dropdown.name === corporateAccountName
      )
    );
    this.setFormControlValue(
      'equipment',
      this.equipmentOptionsList.find(
        dropdown => dropdown.name === equipmentName
      )
    );
  }

  private resetDropdownValues() {
    this.setFormControlValue('corporateAccount', null);
    this.setFormControlValue('equipment', null);
  }

  setContractValidators() {
    const rateValidators: ValidatorFn[] = [
      Validators.required,
      Validators.max(999),
      Validators.min(0.1),
    ];
    if (
      this.workingPeriod.isNewStatusContractor &&
      !this.isCurrentStatusFormer
    ) {
      this.workingPeriodForm.addControl(
        'startDate',
        new FormControl<string | null>(null, [Validators.required])
      );
      this.workingPeriodForm.setValidators(this.contractStartDateValidator());
      this.workingPeriod.setValidators('rate', rateValidators);
    } else {
      this.workingPeriodForm.removeControl('startDate');
      this.workingPeriod.rate.removeValidators(rateValidators);

      this.workingPeriodForm.removeValidators(
        this.contractStartDateValidator()
      );
    }
  }

  formerAccountCorrespondenceControlActions() {
    if (this.isNewStatusFormer)
      this.workingPeriodForm.addControl(
        'accountCorrespondence',
        new FormControl<IDropdownObject | null>(null, [Validators.required])
      );
    else this.workingPeriodForm.removeControl('accountCorrespondence');
  }

  setAccountBlockDateValidator() {
    if (this.isAccountBlockDate) {
      this.workingPeriodForm.addControl(
        'accountBlockDate',
        new FormControl<string | null>(null, [Validators.required])
      );
    } else {
      this.workingPeriodForm.removeControl('accountBlockDate');
    }
  }

  contractStartDateValidator() {
    return (): ValidationErrors | null => {
      const lastWorkingDayValue: string = this.workingPeriod
        .formControl('lastWorkingDay')
        ?.getRawValue();

      const contractStartValue: string = this.workingPeriod
        .formControl('startDate')
        ?.getRawValue();

      const lastWorkingDay: Date | null = lastWorkingDayValue
        ? Utils.formatStringToDate(lastWorkingDayValue)
        : null;

      const contractStart: Date | null = contractStartValue
        ? Utils.formatStringToDate(contractStartValue)
        : null;

      return lastWorkingDay && contractStart && contractStart <= lastWorkingDay
        ? {invalidDate: true}
        : null;
    };
  }

  get isAccountBlockDate(): boolean {
    return (
      this.workingPeriod.formControl('corporateAccount').value?.name ===
      'Specific day and time'
    );
  }

  get isCurrentStatusFormer(): boolean {
    return this.workingPeriod.getCurrentStatus === workingPeriodStatus.Former;
  }

  get isNewStatusFormer(): boolean {
    return this.workingPeriod.newStatus === workingPeriodStatus.Former;
  }

  get equipmentOptionsList(): IDropdownObject[] {
    return this.decreasePeriodDropdowns.equipmentOptionsList;
  }

  get corporateAccountOptionsList(): IDropdownObject[] {
    return this.decreasePeriodDropdowns.corporateAccountOptionsList;
  }

  get terminationReasonOptionsList(): IDropdownObject[] {
    return this.decreasePeriodDropdowns.terminationReasonOptionsList;
  }

  get accountCorrespondenceOptionsList(): IDropdownObject[] {
    return this.decreasePeriodDropdowns.accountCorrespondenceOptionsList;
  }

  setFormControlValue(controlName: string, value: any) {
    this.workingPeriod.formControl(controlName)?.setValue(value);
  }
}
